import { computed, reactive, ref, type Ref } from "vue";
import * as Sentry from "@sentry/vue";

import { useGetProjectSpeckleModelSyncSubscription } from "@/graphql";
import { isVersioningSchemaMissing } from "@/pages/ProjectVersionHistory/services/versioningSchema";

/**
 * `unknown` until the first emission; `unavailable` where the view is not deployed; otherwise what the
 * queue says about this project: no row = `inSync`, a row = `pending` / `syncing` / `failed`.
 */
export type ProjectSpeckleModelSyncStatus = "unknown" | "unavailable" | "inSync" | "pending" | "syncing" | "failed";

/** The queue row as the subscription hands it over (a view, so every column is nullable), reduced to what the status rule reads. */
export type ProjectSpeckleModelSyncRow = {
  state?: string | null;
  requestedAt?: unknown;
  errorMessage?: string | null;
  attempts?: number | null;
};

/**
 * Whether a `failed` row describes an attempt that a manual pass has since made good.
 *
 * The manual sync (`POST integrations/sync`) runs the sweeper's own pass in-request and answers 204
 * once the project has converged, but it never touches the queue row: only the sweeper deletes one, and
 * a failed row first waits out its retry backoff. So after a 204 the row can keep saying "failed" for
 * minutes. In practice that is the sweeper claiming the same request while the manual pass was still
 * creating the model, losing the race to create it ("Could not create branch"), and the retry finding
 * it in place. The row's `requestedAt` names the change that owed the sync: a failure for a request the
 * manual pass had already answered is stale, while a later change bumps `requestedAt` and shows again.
 */
export const isSupersededFailure = (row: ProjectSpeckleModelSyncRow, convergedRequestedAt: string | null): boolean => {
  if (row.state !== "failed" || convergedRequestedAt === null || typeof row.requestedAt !== "string") return false;
  const requested = Date.parse(row.requestedAt);
  const converged = Date.parse(convergedRequestedAt);
  if (Number.isNaN(requested) || Number.isNaN(converged)) return false;
  return requested <= converged;
};

/**
 * The request the last manual pass answered, PER PROJECT. Module-level because the mark is a fact
 * about the project rather than about a component: since RTLCA-5745 two surfaces hold this state at
 * once — the Integrations card owns Sync now and the status line, the Models section nudges a pass
 * after a row is added or removed — and a ref per instance let one of them keep showing a failure
 * the other had already made good.
 */
const convergedRequests = reactive(new Map<string, string>());

/** Marks outlive a component, so a test mounting the composable twice has to start clean. */
export const resetConvergedMarks = () => convergedRequests.clear();

/**
 * Live sync state between a project's model rows and its Speckle stream (RTLCA-5667), from the
 * `projectSpeckleModelSyncRecent` view. Only worth subscribing to while the project holds a stream —
 * without one there is nothing to converge with, so pass that as `enabled`.
 */
export const useProjectSpeckleModelSync = (projectId: Ref<string>, enabled: Ref<boolean>) => {
  const unavailable = ref(false);

  const { result, onError } = useGetProjectSpeckleModelSyncSubscription(
    () => ({ projectId: projectId.value }),
    () => ({
      enabled: enabled.value && projectId.value.length > 0 && !unavailable.value,
      context: { tolerateMissingSchema: true },
    })
  );

  onError((error) => {
    if (isVersioningSchemaMissing(error)) {
      unavailable.value = true;
      return;
    }
    Sentry.captureException(error);
  });

  const row = computed(() => result.value?.projectSpeckleModelSyncRecent?.[0] ?? null);

  // The request the last manual pass answered (its `requestedAt`); see `isSupersededFailure`. Shared
  // per project, so whichever surface ran the pass, every surface reads the same answer.
  const convergedRequestedAt = computed(() => convergedRequests.get(projectId.value) ?? null);

  /**
   * Call once a manual sync pass has answered 204: it ran the sweeper's pass and the project is
   * converged as of now. Remembers the request that pass answered, so a failure the sweeper records for
   * the same request does not read as the sync being broken. With no row in sight there is nothing to
   * supersede, and an earlier mark is kept.
   */
  const markConverged = () => {
    if (typeof row.value?.requestedAt === "string") convergedRequests.set(projectId.value, row.value.requestedAt);
  };

  const superseded = computed(() => !!row.value && isSupersededFailure(row.value, convergedRequestedAt.value));

  const status = computed<ProjectSpeckleModelSyncStatus>(() => {
    if (unavailable.value) return "unavailable";
    if (!result.value) return "unknown";
    if (!row.value || superseded.value) return "inSync";
    if (row.value.state === "failed") return "failed";
    if (row.value.state === "syncing") return "syncing";
    return "pending";
  });

  const errorMessage = computed(() => (superseded.value ? null : row.value?.errorMessage ?? null));
  const attempts = computed(() => row.value?.attempts ?? 0);

  return { status, errorMessage, attempts, markConverged };
};
